import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Save, Lock, Menu, LayoutDashboard, TrendingUp, FileCheck, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available KYC document types
const KYC_DOCUMENT_OPTIONS = [
  { type: 'identity', label: "Pièce d'identité (Recto + Verso)", subTypes: ['identity_front', 'identity_back'] },
  { type: 'proof_address', label: "Justificatif de domicile" },
  { type: 'proof_income', label: "Justificatif de revenus" },
  { type: 'bank_statement', label: "Relevé bancaire" },
  { type: 'iban', label: "IBAN / RIB" },
  { type: 'tax_return', label: "Avis d'imposition" },
  { type: 'other', label: "Autre document" },
];

export default function AdminTemplate() {
  const { token } = useAuth();
  const { clientTemplate, updateClientTemplate } = useSettings();
  const { t } = useLanguage();
  const [template, setTemplate] = useState(clientTemplate);
  const [loading, setLoading] = useState(false);
  const [kycRequirements, setKycRequirements] = useState([]);

  useEffect(() => {
    setTemplate(clientTemplate);
    fetchKycRequirements();
  }, [clientTemplate]);

  const fetchKycRequirements = async () => {
    try {
      const response = await axios.get(`${API}/kyc-requirements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKycRequirements(response.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch KYC requirements');
    }
  };

  const handleChange = (key, value) => {
    setTemplate(prev => ({ ...prev, [key]: value }));
  };

  const toggleKycDocument = (docType) => {
    const doc = KYC_DOCUMENT_OPTIONS.find(d => d.type === docType);
    if (doc?.subTypes) {
      // Grouped type (identity = front + back)
      const allPresent = doc.subTypes.every(st => kycRequirements.some(d => d.type === st));
      if (allPresent) {
        setKycRequirements(kycRequirements.filter(d => !doc.subTypes.includes(d.type)));
      } else {
        const toAdd = doc.subTypes
          .filter(st => !kycRequirements.some(d => d.type === st))
          .map(st => ({ type: st, label: st === 'identity_front' ? "Pièce d'identité — Recto" : "Pièce d'identité — Verso", required: true }));
        setKycRequirements([...kycRequirements, ...toAdd]);
      }
    } else {
      const existing = kycRequirements.find(d => d.type === docType);
      if (existing) {
        setKycRequirements(kycRequirements.filter(d => d.type !== docType));
      } else {
        setKycRequirements([...kycRequirements, { type: docType, label: doc.label, required: true }]);
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Save template
      await updateClientTemplate(template, token);
      
      // Save KYC requirements
      await axios.put(`${API}/kyc-requirements`, { documents: kycRequirements }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('success'), { description: 'Template et KYC mis à jour' });
    } catch (error) {
      toast.error(t('error'), { description: 'Erreur lors de la sauvegarde' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('client_template')}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              Définissez les paramètres par défaut pour tous les clients
            </p>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={loading}
            className="btn-gold"
            data-testid="save-template-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? t('loading') : t('save')}
          </Button>
        </div>

        <Tabs defaultValue="auth" className="space-y-6">
          <TabsList className="glass flex-wrap">
            <TabsTrigger value="auth" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Lock className="w-4 h-4 mr-2" />
              Authentification
            </TabsTrigger>
            <TabsTrigger value="menu" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Menu className="w-4 h-4 mr-2" />
              Menus
            </TabsTrigger>
            <TabsTrigger value="widgets" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Widgets
            </TabsTrigger>
            <TabsTrigger value="trading" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <TrendingUp className="w-4 h-4 mr-2" />
              Trading
            </TabsTrigger>
            <TabsTrigger value="kyc" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <FileCheck className="w-4 h-4 mr-2" />
              KYC
            </TabsTrigger>
          </TabsList>

          {/* Authentication */}
          <TabsContent value="auth">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">{t('authentication')}</CardTitle>
                <CardDescription>Paramètres de sécurité et connexion</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t('pin_enabled')}</Label>
                    <p className="text-sm text-muted-foreground">
                      Activer le code PIN avant connexion
                    </p>
                  </div>
                  <Switch
                    checked={template.pin_enabled}
                    onCheckedChange={(checked) => handleChange('pin_enabled', checked)}
                    data-testid="pin-enabled-switch"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t('pin_length')}</Label>
                    <Select
                      value={String(template.pin_length)}
                      onValueChange={(value) => handleChange('pin_length', parseInt(value))}
                    >
                      <SelectTrigger className="bg-card" data-testid="pin-length-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="4">4 chiffres</SelectItem>
                        <SelectItem value="6">6 chiffres</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tentatives max PIN</Label>
                    <Input
                      type="number"
                      value={template.pin_max_attempts}
                      onChange={(e) => handleChange('pin_max_attempts', parseInt(e.target.value))}
                      min={1}
                      max={10}
                      className="bg-card"
                      data-testid="pin-max-attempts-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('session_timeout')} (minutes)</Label>
                  <Input
                    type="number"
                    value={template.session_timeout_minutes}
                    onChange={(e) => handleChange('session_timeout_minutes', parseInt(e.target.value))}
                    min={5}
                    max={120}
                    className="bg-card"
                    data-testid="session-timeout-input"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Menu Visibility */}
          <TabsContent value="menu">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">{t('menu_visibility')}</CardTitle>
                <CardDescription>Contrôlez les menus et sous-menus visibles pour les clients</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* ACCUEIL */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Accueil</h4>
                  <div className="space-y-2 pl-2 border-l-2 border-[#D4AF37]/30">
                    {[
                      { key: 'menu_dashboard', label: 'Tableau de bord' },
                      { key: 'menu_news', label: 'Actualités' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <Label className="text-sm">{item.label}</Label>
                        <Switch
                          checked={template[item.key] !== false}
                          onCheckedChange={(checked) => handleChange(item.key, checked)}
                          data-testid={`${item.key}-switch`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* INVESTISSEMENTS */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Investissements</h4>
                  <div className="space-y-2 pl-2 border-l-2 border-[#D4AF37]/30">
                    {[
                      { key: 'menu_investments', label: 'Placement' },
                      { key: 'menu_my_investments', label: 'Mes Investissements' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <Label className="text-sm">{item.label}</Label>
                        <Switch
                          checked={template[item.key] !== false}
                          onCheckedChange={(checked) => handleChange(item.key, checked)}
                          data-testid={`${item.key}-switch`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* SIMULATION */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Simulation</h4>
                  <div className="space-y-2 pl-2 border-l-2 border-[#D4AF37]/30">
                    {[
                      { key: 'menu_simulator', label: 'Simulateur de gains' },
                      { key: 'menu_simulation', label: 'Prévisualisation' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <Label className="text-sm">{item.label}</Label>
                        <Switch
                          checked={template[item.key] !== false}
                          onCheckedChange={(checked) => handleChange(item.key, checked)}
                          data-testid={`${item.key}-switch`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* ARBITRAGE */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Arbitrage</h4>
                  <div className="space-y-2 pl-2 border-l-2 border-[#D4AF37]/30">
                    <div className="flex items-center justify-between py-2">
                      <Label className="text-sm">Salle des marchés</Label>
                      <Switch
                        checked={template.menu_trade !== false}
                        onCheckedChange={(checked) => handleChange('menu_trade', checked)}
                        data-testid="menu_trade-switch"
                      />
                    </div>
                  </div>
                </div>

                {/* BOURSE - With sub-menus */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Bourse</h4>
                    <Switch
                      checked={template.menu_stock !== false}
                      onCheckedChange={(checked) => handleChange('menu_stock', checked)}
                      data-testid="menu_stock-switch"
                    />
                  </div>
                  {template.menu_stock !== false && (
                    <div className="space-y-2 pl-4 border-l-2 border-[#D4AF37]/30 bg-secondary/20 rounded-r-lg py-2">
                      <p className="text-xs text-muted-foreground mb-2">Sous-catégories</p>
                      {[
                        { key: 'submenu_bourse_salle', label: 'Salle des marchés' },
                        { key: 'submenu_bourse_epargnes', label: 'Les épargnes' },
                        { key: 'submenu_bourse_portefeuille', label: 'Mon portefeuille' },
                        { key: 'submenu_bourse_actions', label: 'Mes Actions' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center justify-between py-1.5">
                          <Label className="text-sm text-muted-foreground">{item.label}</Label>
                          <Switch
                            checked={template[item.key] !== false}
                            onCheckedChange={(checked) => handleChange(item.key, checked)}
                            data-testid={`${item.key}-switch`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* CRYPTO - With sub-menus */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Crypto</h4>
                    <Switch
                      checked={template.menu_crypto !== false}
                      onCheckedChange={(checked) => handleChange('menu_crypto', checked)}
                      data-testid="menu_crypto-switch"
                    />
                  </div>
                  {template.menu_crypto !== false && (
                    <div className="space-y-2 pl-4 border-l-2 border-[#D4AF37]/30 bg-secondary/20 rounded-r-lg py-2">
                      <p className="text-xs text-muted-foreground mb-2">Sous-catégories</p>
                      {[
                        { key: 'submenu_crypto_salle', label: 'Acheter des cryptos' },
                        { key: 'submenu_crypto_portfolio', label: 'Mes Cryptos' },
                        { key: 'submenu_crypto_epargnes', label: 'Les épargnes' },
                      ].map(item => (
                        <div key={item.key} className="flex items-center justify-between py-1.5">
                          <Label className="text-sm text-muted-foreground">{item.label}</Label>
                          <Switch
                            checked={template[item.key] !== false}
                            onCheckedChange={(checked) => handleChange(item.key, checked)}
                            data-testid={`${item.key}-switch`}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Mon Wallet - separate from main crypto toggle */}
                  <div className="space-y-2 pl-2 border-l-2 border-[#D4AF37]/30">
                    <div className="flex items-center justify-between py-2">
                      <Label className="text-sm">Mon wallet</Label>
                      <Switch
                        checked={template.menu_wallets !== false}
                        onCheckedChange={(checked) => handleChange('menu_wallets', checked)}
                        data-testid="menu_wallets-switch"
                      />
                    </div>
                  </div>
                </div>

                {/* MON ESPACE */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wide">Mon Espace</h4>
                  <div className="space-y-2 pl-2 border-l-2 border-[#D4AF37]/30">
                    {[
                      { key: 'menu_transactions', label: 'Historique' },
                      { key: 'menu_messages', label: 'Messagerie' },
                      { key: 'menu_documents', label: 'Mes documents' },
                      { key: 'menu_profile', label: 'Profil' },
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between py-2">
                        <Label className="text-sm">{item.label}</Label>
                        <Switch
                          checked={template[item.key] !== false}
                          onCheckedChange={(checked) => handleChange(item.key, checked)}
                          data-testid={`${item.key}-switch`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dashboard Widgets */}
          <TabsContent value="widgets">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">{t('dashboard_widgets')}</CardTitle>
                <CardDescription>Widgets affichés sur le tableau de bord client</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: 'widget_wallets', label: 'Portefeuilles' },
                  { key: 'widget_portfolios', label: 'Répartition des actifs' },
                  { key: 'widget_balances', label: 'Soldes' },
                  { key: 'widget_gains', label: 'Gains/Pertes' },
                  { key: 'widget_progress', label: 'Progression' },
                  { key: 'widget_analyst_card', label: 'Carte analyste' },
                  { key: 'widget_testimonials', label: 'Témoignages' },
                ].map(item => (
                  <div key={item.key} className="flex items-center justify-between py-2 border-b border-border">
                    <Label>{item.label}</Label>
                    <Switch
                      checked={template[item.key]}
                      onCheckedChange={(checked) => handleChange(item.key, checked)}
                      data-testid={`${item.key}-switch`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trading Settings */}
          <TabsContent value="trading">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">{t('trading_settings')}</CardTitle>
                <CardDescription>Paramètres de trading par défaut (simulation)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Trading Portfolio Toggle */}
                <div className="p-4 rounded-lg border-2 border-purple-500/30 bg-purple-500/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-purple-400 font-semibold">Portefeuille Trading</Label>
                      <p className="text-sm text-muted-foreground">
                        Activer le portefeuille trading séparé pour tous les clients
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Les clients pourront transférer des fonds vers un portefeuille dédié au trading
                      </p>
                    </div>
                    <Switch
                      checked={template.trading_portfolio_enabled}
                      onCheckedChange={(checked) => handleChange('trading_portfolio_enabled', checked)}
                      data-testid="trading-portfolio-switch"
                      className="data-[state=checked]:bg-purple-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t('manual_trading')}</Label>
                    <p className="text-sm text-muted-foreground">
                      Autoriser les prises de positions manuelles
                    </p>
                  </div>
                  <Switch
                    checked={template.trading_manual_allowed}
                    onCheckedChange={(checked) => handleChange('trading_manual_allowed', checked)}
                    data-testid="trading-manual-switch"
                  />
                </div>

                {template.trading_manual_allowed && (
                  <div className="flex items-center justify-between ml-6 pl-4 border-l-2 border-border">
                    <div>
                      <Label>Horaires de marché</Label>
                      <p className="text-sm text-muted-foreground">
                        Désactiver les boutons Acheter/Vendre quand le marché est fermé (hors 9h-22h)
                      </p>
                    </div>
                    <Switch
                      checked={template.trading_market_hours_enabled || false}
                      onCheckedChange={(checked) => handleChange('trading_market_hours_enabled', checked)}
                      data-testid="trading-market-hours-switch"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label>{t('auto_trading')}</Label>
                    <p className="text-sm text-muted-foreground">
                      Autoriser le trading automatique
                    </p>
                  </div>
                  <Switch
                    checked={template.trading_auto_allowed}
                    onCheckedChange={(checked) => handleChange('trading_auto_allowed', checked)}
                    data-testid="trading-auto-switch"
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>{t('max_leverage')}</Label>
                    <Select
                      value={String(template.trading_max_leverage)}
                      onValueChange={(value) => handleChange('trading_max_leverage', parseInt(value))}
                    >
                      <SelectTrigger className="bg-card" data-testid="max-leverage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">x1</SelectItem>
                        <SelectItem value="10">x10</SelectItem>
                        <SelectItem value="25">x25</SelectItem>
                        <SelectItem value="50">x50</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('max_exposure')} (%)</Label>
                    <Input
                      type="number"
                      value={template.trading_max_exposure}
                      onChange={(e) => handleChange('trading_max_exposure', parseFloat(e.target.value))}
                      min={0}
                      max={100}
                      step={5}
                      className="bg-card"
                      data-testid="max-exposure-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>État initial du trading</Label>
                  <Select
                    value={template.trading_default_state}
                    onValueChange={(value) => handleChange('trading_default_state', value)}
                  >
                    <SelectTrigger className="bg-card" data-testid="trading-state-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neutral">Neutre</SelectItem>
                      <SelectItem value="active">Actif</SelectItem>
                      <SelectItem value="suspended">Suspendu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* KYC Settings */}
          <TabsContent value="kyc">
            <Card className="card-premium">
              <CardHeader>
                <CardTitle className="text-lg">{t('kyc')}</CardTitle>
                <CardDescription>Sélectionnez les documents requis pour les clients</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Document Selection */}
                <div className="space-y-4">
                  <Label className="text-base font-semibold">Documents à demander aux clients</Label>
                  <p className="text-sm text-muted-foreground">
                    Sélectionnez les documents obligatoires pour compléter la vérification KYC
                  </p>
                  
                  <div className="grid grid-cols-1 gap-3">
                    {KYC_DOCUMENT_OPTIONS.map(doc => {
                      const isSelected = doc.subTypes
                        ? doc.subTypes.every(st => kycRequirements.some(d => d.type === st))
                        : kycRequirements.some(d => d.type === doc.type);
                      return (
                        <div 
                          key={doc.type}
                          className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                              : 'border-border hover:border-[#D4AF37]/50'
                          }`}
                          onClick={() => toggleKycDocument(doc.type)}
                          data-testid={`kyc-doc-${doc.type}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              isSelected ? 'bg-[#D4AF37] text-black' : 'bg-secondary'
                            }`}>
                              {isSelected && <CheckCircle className="w-4 h-4" />}
                            </div>
                            <span className={isSelected ? 'font-medium' : ''}>{doc.label}</span>
                          </div>
                          <Switch
                            checked={isSelected}
                            onCheckedChange={() => toggleKycDocument(doc.type)}
                          />
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="p-3 rounded-lg bg-secondary/30 text-sm">
                    <strong>Documents sélectionnés:</strong> {kycRequirements.length > 0 
                      ? kycRequirements.map(d => d.label).join(', ')
                      : 'Aucun document requis'}
                  </div>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Bloquer les retraits</Label>
                      <p className="text-sm text-muted-foreground">
                        Bloquer les retraits si KYC incomplet
                      </p>
                    </div>
                    <Switch
                      checked={template.kyc_block_withdrawals}
                      onCheckedChange={(checked) => handleChange('kyc_block_withdrawals', checked)}
                      data-testid="kyc-block-switch"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Devise par défaut</Label>
                    <Select
                      value={template.default_currency}
                      onValueChange={(value) => handleChange('default_currency', value)}
                    >
                      <SelectTrigger className="bg-card" data-testid="currency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="USD">USD - Dollar</SelectItem>
                        <SelectItem value="GBP">GBP - Livre</SelectItem>
                        <SelectItem value="CHF">CHF - Franc Suisse</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Multi-devises</Label>
                    <div className="flex items-center h-10">
                      <Switch
                        checked={template.multi_currency}
                        onCheckedChange={(checked) => handleChange('multi_currency', checked)}
                        data-testid="multi-currency-switch"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
